import type { MarketingNote } from '../../../../api/venue-marketing';

import { useDeleteMarketingNote, useUpdateMarketingNote } from './use-marketing-notes';
import { marketingErrorMessage, type MarketingAction } from './venue-marketing-errors';

/**
 * The three things a person can do to an existing note. Each resolves to true
 * when it worked. A failure is reported once through `onError`, with its own
 * wording, so the list can show it even after a refresh has removed the note.
 */
export function useNoteActions(
  venueId: string,
  onError: (message: string) => void,
  regionName: string | null,
) {
  const update = useUpdateMarketingNote(venueId);
  const remove = useDeleteMarketingNote(venueId);

  const attempt = async (run: () => Promise<unknown>, action: MarketingAction) => {
    try {
      await run();
      return true;
    } catch (err) {
      onError(marketingErrorMessage(err, action, regionName));
      return false;
    }
  };

  return {
    busy: update.isPending || remove.isPending,
    toggleDone: (note: MarketingNote) =>
      attempt(
        () => update.mutateAsync({ noteId: note.id, patch: { isDone: !note.isDone } }),
        'toggle-note',
      ),
    saveBody: (note: MarketingNote, body: string) =>
      attempt(() => update.mutateAsync({ noteId: note.id, patch: { body } }), 'edit-note'),
    remove: (note: MarketingNote) => attempt(() => remove.mutateAsync(note.id), 'delete-note'),
  };
}
